import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchReferralOrders } from '../../services/referralService';

const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const dateTime = (value) => value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
const statusColor = { approved: 'success', pending: 'warning', rejected: 'error', cancelled: 'default' };

export default function ReferralOrders() {
  const navigate = useNavigate();
  const [data, setData] = useState({ summary: {}, accounts: [], orders: [], pagination: { page: 1, total_pages: 1 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchReferralOrders({ page, limit: 25, status: status || undefined, search: search || undefined });
      setData(response);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load referral orders.');
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    const timer = window.setTimeout(load, 300);
    return () => window.clearTimeout(timer);
  }, [load]);

  const summaryCards = [
    ['Referral orders', data.summary.total_orders],
    ['Approved', data.summary.approved_orders],
    ['Pending', data.summary.pending_orders],
    ['Active referrers', data.summary.total_accounts],
    ['Friend discounts', money(data.summary.total_friend_discount)],
    ['Credits earned', money(data.summary.approved_referrer_credit)],
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={900}>Refer & Earn</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5 }}>Referral orders, customer rewards, and earned credits in one place.</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2, mt: 3 }}>
        {summaryCards.map(([label, value]) => (
          <Card key={label} variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>{value || 0}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3, mb: 2 }}>
        <TextField
          size="small"
          label="Search code, customer, referrer, or order"
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          sx={{ minWidth: { sm: 360 } }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Referral links</Typography>
      <TableContainer sx={{ mb: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Referrer</TableCell><TableCell>Referral link</TableCell><TableCell>Used</TableCell><TableCell align="right">Balance</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>{data.accounts?.length ? data.accounts.map((account, index) => (
            <TableRow key={`${account.id || account.user_id || 'account'}-${index}`}>
              <TableCell><Typography fontWeight={700}>{account.name || account.email || '-'}</Typography><Typography variant="caption" color="text.secondary">{account.email || ''}</Typography></TableCell>
              <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>/?ref={account.referral_code}</Typography></TableCell>
              <TableCell>{account.used_count || 0}</TableCell>
              <TableCell align="right">{money(account.credit_balance)}</TableCell>
              <TableCell><Chip size="small" label={account.status} color={account.status === 'active' ? 'success' : 'default'} /></TableCell>
            </TableRow>
          )) : <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No referral links found.</TableCell></TableRow>}</TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Used referral orders</Typography>

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Referrer</TableCell>
              <TableCell>Referred customer</TableCell>
              <TableCell>Order</TableCell>
              <TableCell align="right">Order total</TableCell>
              <TableCell align="right">Friend saved</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : data.orders.length ? data.orders.map((order) => (
              <TableRow key={order.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(order.created_at)}</TableCell>
                <TableCell><Typography fontWeight={800} variant="body2">{order.referral_code}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{order.referrer_name || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{order.referrer_email || ''}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{order.referred_user_name || order.referred_email || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{order.referred_phone || ''}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    color="primary"
                    sx={{ cursor: (order.order_id || order.work_order_id) ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (order.order_id) navigate(`/orders/${order.order_id}`);
                      else if (order.work_order_id) navigate(`/workorders/${order.work_order_id}`);
                    }}
                  >
                    {order.order_number || order.work_order_number || `#${order.order_id || order.work_order_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{order.payment_status || order.work_order_status || ''}</Typography>
                </TableCell>
                <TableCell align="right">{money(order.order_total)}</TableCell>
                <TableCell align="right">{money(order.friend_discount)}</TableCell>
                <TableCell align="right">{money(order.referrer_credit)}</TableCell>
                <TableCell><Chip size="small" label={order.status} color={statusColor[order.status] || 'default'} /></TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>No referral orders found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {Number(data.pagination?.total_pages || 1) > 1 ? (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination count={data.pagination.total_pages} page={page} onChange={(_event, value) => setPage(value)} />
        </Stack>
      ) : null}
    </Box>
  );
}
